import type { ApiResponse } from "@/types";
import type { MCPServersState } from "agents";
import type {
  AgentResponse,
  CreateAgentRequest,
  SlackAppConfig,
  SlackAppCredentials,
  ToolUpdateRequest,
  ToolsResponse,
  UpdateAgentRequest,
} from "../../../pavilion/schemas";
import {
  ApiResponseSchema,
  type AppFeedbackRequest,
  type AppFeedbackResponse,
  type CreateFeedbackRequest,
  type CreateFeedbackResponse,
  type ListFeedbackResponse,
  type ProfileResponse,
} from "../../../schemas";
import {
  type AddMcpServer,
  type AgentTask,
  type CreateChatRequest,
  type CreateChatResponse,
  type FetchEndpointRequestData,
  type FetchEndpointResponse,
  type GetChatResponse,
  type GetDeploymentResponse,
  GetDeploymentResponseSchema,
  type GetEndpointsResponse,
  type ListChatResponse,
  type ListDeploymentsResponse,
  ListDeploymentsResponseSchema,
  type ListMessagesResponse,
  type MutateSecret,
  type RemoveMcpServer,
  type SecretMutation,
  type SerializableMcpConnection,
} from "../../../schemas/codegen";
// Using AgentResponse from pavilion/schemas instead of duplicate Agent interface
import { chatWebSocketManager } from "../websocket/ChatWebSocketManager";
import type { CreateFeedbackParams } from "./feedback";
import { baseFetch } from "./fetch";

export const api = {
  getProfile: async (): Promise<ApiResponse<ProfileResponse>> => {
    return baseFetch<ApiResponse<ProfileResponse>>("/api/auth/profile");
  },

  listChats: async (): Promise<ApiResponse<ListChatResponse>> => {
    return baseFetch<ApiResponse<ListChatResponse>>("/api/chats");
  },

  getChat: async (chatId: string): Promise<ApiResponse<GetChatResponse>> => {
    return baseFetch<ApiResponse<GetChatResponse>>(`/api/chats/${chatId}`);
  },

  createChat: async (
    data: CreateChatRequest,
  ): Promise<ApiResponse<CreateChatResponse>> => {
    return baseFetch<ApiResponse<CreateChatResponse>>("/api/chats", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(data),
    });
  },

  archiveChat: async (
    chatId: string,
  ): Promise<ApiResponse<GetChatResponse>> => {
    return baseFetch<ApiResponse<GetChatResponse>>(
      `/api/chats/${chatId}/archive`,
      {
        method: "POST",
        credentials: "include",
      },
    );
  },

  listMessages: async (
    chatId: string,
  ): Promise<ApiResponse<ListMessagesResponse>> => {
    return baseFetch<ApiResponse<ListMessagesResponse>>(
      `/api/chats/${chatId}/messages`,
    );
  },

  getEndpoints: async (
    chatId: string,
  ): Promise<ApiResponse<GetEndpointsResponse>> => {
    return baseFetch<ApiResponse<GetEndpointsResponse>>(
      `/api/chats/${chatId}/endpoints`,
    );
  },

  fetchEndpointRequest: async (
    chatId: string,
    requestData: FetchEndpointRequestData,
  ): Promise<FetchEndpointResponse> => {
    const response = await baseFetch<ApiResponse<FetchEndpointResponse>>(
      `/api/chats/${chatId}/fetch-endpoint`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(requestData),
      },
    );
    return response.data;
  },

  /**
   * List all of a user's feedback on messages for a chat
   */
  listChatFeedback: async (
    chatId: string,
  ): Promise<ApiResponse<ListFeedbackResponse>> => {
    return baseFetch<ApiResponse<ListFeedbackResponse>>(
      `/api/feedback?chatId=${chatId}`,
      {
        method: "GET",
        credentials: "include",
      },
    );
  },

  /**
   * Give a thumbs up to a message
   */
  giveThumbsUp: async (
    params: CreateFeedbackParams,
  ): Promise<ApiResponse<CreateFeedbackResponse>> => {
    const feedback: CreateFeedbackRequest = {
      id: params.id,
      chatId: params.chatId,
      messageId: params.messageId,
      score: 1,
      text: params.additionalUserInput,
    };
    return baseFetch<ApiResponse<CreateFeedbackResponse>>("/api/feedback", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(feedback),
    });
  },

  /**
   * Give a thumbs down to a message
   */
  giveThumbsDown: async (
    params: CreateFeedbackParams,
  ): Promise<ApiResponse<CreateFeedbackResponse>> => {
    const feedback: CreateFeedbackRequest = {
      id: params.id,
      chatId: params.chatId,
      messageId: params.messageId,
      score: 0,
      text: params.additionalUserInput,
    };
    return baseFetch<ApiResponse<CreateFeedbackResponse>>("/api/feedback", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(feedback),
    });
  },

  /**
   * Undo a thumbs up or thumbs down
   */
  undoFeedback: async (
    feedbackId: string,
  ): Promise<ApiResponse<CreateFeedbackResponse>> => {
    return baseFetch<ApiResponse<CreateFeedbackResponse>>(
      `/api/feedback/${feedbackId}`,
      {
        method: "DELETE",
        credentials: "include",
      },
    );
  },

  /**
   * Log out the current user
   */
  logout: async (): Promise<void> => {
    return baseFetch<void>("/api/auth/logout", {
      method: "POST",
      credentials: "include",
    });
  },

  /**
   * Secrets/Environment Variables API
   */
  listSecrets: async (chatId: string) => {
    return baseFetch<ApiResponse<{ secretKeys: string[] }>>(
      `/api/chats/${chatId}/secrets`,
    );
  },

  /**
   * Mutate secrets via CHAT WebSocket
   */
  mutateSecrets: async (
    chatId: string,
    mutations: SecretMutation[],
  ): Promise<boolean> => {
    const connection = chatWebSocketManager.getConnection(chatId);

    const mutationCommand: MutateSecret = {
      type: "secret.mutate",
      mutations,
    };

    // Ensure connection is established
    if (!connection.isConnected()) {
      try {
        await connection.connect();
      } catch (error) {
        console.error(
          `Failed to connect to chat ${chatId} for secret mutation:`,
          error,
        );
        return false;
      }
    }

    // Send mutation command via CHAT WebSocket
    return connection.send(mutationCommand);
  },

  /**
   * Add/update a secret via CHAT WebSocket
   */
  setSecret: async (
    chatId: string,
    secretKey: string,
    secretValue: string,
  ): Promise<boolean> => {
    return api.mutateSecrets(chatId, [
      { type: "secret.set", secretKey, secretValue },
    ]);
  },

  /**
   * Delete a secret via CHAT WebSocket
   */
  deleteSecret: async (chatId: string, secretKey: string): Promise<boolean> => {
    return api.mutateSecrets(chatId, [{ type: "secret.delete", secretKey }]);
  },

  /**
   * List MCP server connections for a chat
   */
  listMcpServers: async (
    chatId: string,
  ): Promise<
    ApiResponse<{ connections: Record<string, SerializableMcpConnection> }>
  > => {
    return baseFetch<
      ApiResponse<{ connections: Record<string, SerializableMcpConnection> }>
    >(`/api/chats/${chatId}/mcp/servers`);
  },

  /**
   * List tasks for a chat session
   */
  listTasks: async (
    chatId: string,
  ): Promise<ApiResponse<{ tasks: AgentTask[] }>> => {
    return baseFetch<ApiResponse<{ tasks: AgentTask[] }>>(
      `/api/chats/${chatId}/tasks`,
    );
  },

  /**
   * Add MCP server via CHAT WebSocket
   */
  addChatMcpServer: async (
    chatId: string,
    url: string,
    name: string,
  ): Promise<boolean> => {
    const connection = chatWebSocketManager.getConnection(chatId);

    const addServerCommand: AddMcpServer = {
      type: "mcp.server.add",
      url,
      name,
    };

    // Ensure connection is established
    if (!connection.isConnected()) {
      try {
        await connection.connect();
      } catch (error) {
        console.error(
          `Failed to connect to chat ${chatId} for MCP server add:`,
          error,
        );
        return false;
      }
    }

    // Send add server command via CHAT WebSocket
    return connection.send(addServerCommand);
  },

  /**
   * Remove MCP server via CHAT WebSocket
   */
  removeChatMcpServer: async (
    chatId: string,
    serverId: string,
  ): Promise<boolean> => {
    const connection = chatWebSocketManager.getConnection(chatId);

    const removeServerCommand: RemoveMcpServer = {
      type: "mcp.server.remove",
      serverId,
    };

    // Ensure connection is established
    if (!connection.isConnected()) {
      try {
        await connection.connect();
      } catch (error) {
        console.error(
          `Failed to connect to chat ${chatId} for MCP server remove:`,
          error,
        );
        return false;
      }
    }

    // Send remove server command via CHAT WebSocket
    return connection.send(removeServerCommand);
  },

  /**
   * Submit app feedback
   */
  submitAppFeedback: async (
    data: AppFeedbackRequest,
  ): Promise<ApiResponse<AppFeedbackResponse>> => {
    return baseFetch<ApiResponse<AppFeedbackResponse>>("/api/app-feedback", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(data),
    });
  },

  /**
   * Agents API
   */
  listAgents: async (): Promise<ApiResponse<{ agents: AgentResponse[] }>> => {
    return baseFetch<ApiResponse<{ agents: AgentResponse[] }>>("/api/agents");
  },

  createAgent: async (
    data: CreateAgentRequest,
  ): Promise<ApiResponse<AgentResponse>> => {
    return baseFetch<ApiResponse<AgentResponse>>("/api/agents", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(data),
    });
  },

  getAgent: async (agentName: string): Promise<ApiResponse<AgentResponse>> => {
    return baseFetch<ApiResponse<AgentResponse>>(
      `/api/agents/${encodeURIComponent(agentName)}`,
    );
  },

  updateAgent: async (
    agentName: string,
    data: UpdateAgentRequest,
  ): Promise<
    ApiResponse<{
      config: {
        id: string;
        name: string;
        systemPrompt: string;
        description?: string;
        modelProvider: string;
        modelName: string;
        createdAt: string;
        updatedAt: string;
        version: number;
      };
    }>
  > => {
    return baseFetch<
      ApiResponse<{
        config: {
          id: string;
          name: string;
          systemPrompt: string;
          description?: string;
          modelProvider: string;
          modelName: string;
          createdAt: string;
          updatedAt: string;
          version: number;
        };
      }>
    >(`/api/agents/${encodeURIComponent(agentName)}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(data),
    });
  },

  deleteAgent: async (agentName: string): Promise<void> => {
    await baseFetch<void>(
      `/api/agents/${encodeURIComponent(agentName)}`,
      {
        method: "DELETE",
        credentials: "include",
      },
      async (response) => {
        // For 204 No Content, don't try to parse JSON
        if (response.status === 204) {
          return undefined;
        }
        return response.json();
      },
    );
  },

  listAgentConversations: async (
    agentName: string,
  ): Promise<
    ApiResponse<{
      conversations: Array<{
        id: string;
        agentId: string;
        title?: string;
        createdAt: string;
        updatedAt: string;
      }>;
    }>
  > => {
    return baseFetch<
      ApiResponse<{
        conversations: Array<{
          id: string;
          agentId: string;
          title?: string;
          createdAt: string;
          updatedAt: string;
        }>;
      }>
    >(`/api/agents/${encodeURIComponent(agentName)}/conversations`);
  },

  createAgentConversation: async (
    agentName: string,
    data: {
      title?: string;
    },
  ): Promise<
    ApiResponse<{
      conversation: {
        id: string;
        agentId: string;
        title?: string;
        createdAt: string;
        updatedAt: string;
      };
    }>
  > => {
    return baseFetch<
      ApiResponse<{
        conversation: {
          id: string;
          agentId: string;
          title?: string;
          createdAt: string;
          updatedAt: string;
        };
      }>
    >(`/api/agents/${encodeURIComponent(agentName)}/conversations`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(data),
    });
  },

  listAgentTools: async (
    agentName: string,
  ): Promise<ApiResponse<ToolsResponse["data"]>> => {
    return baseFetch<ApiResponse<ToolsResponse["data"]>>(
      `/api/agents/${encodeURIComponent(agentName)}/tools`,
    );
  },

  updateAgentTools: async (
    agentName: string,
    tools: ToolUpdateRequest["tools"],
  ): Promise<ApiResponse<{ success: boolean }>> => {
    return baseFetch<ApiResponse<{ success: boolean }>>(
      `/api/agents/${encodeURIComponent(agentName)}/tools`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ tools }),
      },
    );
  },

  getMcpServers: async (
    agentName: string,
  ): Promise<ApiResponse<MCPServersState>> => {
    return baseFetch(
      `/api/agents/${encodeURIComponent(agentName)}/mcp/servers`,
      {
        method: "GET",
        credentials: "include",
      },
    );
  },

  addMcpServer: async (
    agentName: string,
    data: {
      url: string;
      name: string;
    },
  ): Promise<ApiResponse<{ serverId: string; authUrl?: string }>> => {
    return baseFetch<ApiResponse<{ serverId: string; authUrl?: string }>>(
      `/api/agents/${encodeURIComponent(agentName)}/mcp/servers`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(data),
      },
    );
  },

  removeMcpServer: async (
    agentName: string,
    serverId: string,
  ): Promise<ApiResponse<{ success: boolean }>> => {
    return baseFetch<ApiResponse<{ success: boolean }>>(
      `/api/agents/${encodeURIComponent(agentName)}/mcp/servers/${encodeURIComponent(serverId)}`,
      {
        method: "DELETE",
        credentials: "include",
      },
    );
  },

  /**
   * Slack Integration API
   */
  getSlackConfig: async (
    agentName: string,
  ): Promise<ApiResponse<SlackAppConfig | null>> => {
    return baseFetch<ApiResponse<SlackAppConfig | null>>(
      `/api/slack/${encodeURIComponent(agentName)}/config`,
    );
  },

  getSlackManifestUrl: async (
    agentName: string,
  ): Promise<
    ApiResponse<{
      manifestUrl: string;
      publicDomain: string;
      isLocalhost: boolean;
    }>
  > => {
    return baseFetch<
      ApiResponse<{
        manifestUrl: string;
        publicDomain: string;
        isLocalhost: boolean;
      }>
    >(`/api/slack/${encodeURIComponent(agentName)}/manifest-url`);
  },

  storeSlackCredentials: async (
    agentName: string,
    credentials: SlackAppCredentials,
  ): Promise<ApiResponse<{ success: boolean; installUrl: string }>> => {
    return baseFetch<ApiResponse<{ success: boolean; installUrl: string }>>(
      `/api/slack/${encodeURIComponent(agentName)}/store-credentials`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(credentials),
      },
    );
  },

  removeSlackWorkspace: async (
    agentName: string,
    teamId: string,
  ): Promise<ApiResponse<{ success: boolean }>> => {
    return baseFetch<ApiResponse<{ success: boolean }>>(
      `/api/slack/${encodeURIComponent(agentName)}/workspace/${encodeURIComponent(teamId)}`,
      {
        method: "DELETE",
        credentials: "include",
      },
    );
  },

  listDeployments: async (): Promise<ApiResponse<ListDeploymentsResponse>> => {
    return baseFetch("/api/deployments", undefined, async (resp) => {
      const body = await resp.json();
      return ApiResponseSchema(ListDeploymentsResponseSchema).parse(body);
    });
  },

  getDeployment: async (
    chatId: string,
    deploymentId: string,
  ): Promise<ApiResponse<GetDeploymentResponse>> => {
    return baseFetch(
      `/api/chats/${chatId}/deployments/${deploymentId}`,
      undefined,
      async (resp) => {
        const body = await resp.json();
        return ApiResponseSchema(GetDeploymentResponseSchema).parse(body);
      },
    );
  },
};
